import React, { useMemo } from 'react';

/**
 * CarnivalBackground
 * A fixed, animated, colorful backdrop for the homepage inspired by
 * Brasilian carnival — magenta, gold, teal, violet, orange. Sits behind
 * all content (z-index -10) and does NOT touch any media. It combines:
 *   1. A slow multi-radial gradient that shifts across the palette.
 *   2. A conic sheen that rotates for a sparkly-parade feel.
 *   3. Fine grain noise to keep the surface from banding.
 *   4. Twinkling star particles randomly positioned.
 */
export const CarnivalBackground = () => {
  // Pre-compute a set of sparkle positions once so the render is stable.
  const sparkles = useMemo(() => {
    const arr = [];
    const count = 72;
    for (let i = 0; i < count; i++) {
      arr.push({
        id: i,
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: 3 + Math.random() * 6,
        delay: Math.random() * 6,
        duration: 2.5 + Math.random() * 4,
        hue: [340, 45, 175, 285, 20, 60][i % 6], // magenta, gold, teal, violet, orange, yellow
      });
    }
    return arr;
  }, []);

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
      data-testid="carnival-background"
    >
      {/* Base deep-plum backdrop so text stays legible even before gradients paint */}
      <div className="absolute inset-0 bg-[#160820]" />

      {/* Animated multi-radial gradient */}
      <div className="absolute inset-0 carnival-gradient" />

      {/* Slow rotating conic sheen for parade-glow */}
      <div className="absolute inset-0 carnival-conic opacity-40 mix-blend-screen" />

      {/* Subtle grain texture (keeps gradients from banding) */}
      <div className="absolute inset-0 noise-texture opacity-[0.12]" />

      {/* Sparkle field */}
      <div className="absolute inset-0">
        {sparkles.map(s => (
          <span
            key={s.id}
            className="carnival-sparkle"
            style={{
              top: `${s.top}%`,
              left: `${s.left}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
              background: `hsl(${s.hue} 95% 70%)`,
              boxShadow: `0 0 ${s.size * 3}px hsl(${s.hue} 95% 65% / 0.9)`,
            }}
          />
        ))}
      </div>

      {/* Soft vignette so edges settle and center content pops */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(0,0,0,0.55)_100%)]" />
    </div>
  );
};

export default CarnivalBackground;
